import { FunctionComponent, memo, useCallback, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ChannelAppTabAction,
  getChannelUrl,
  NotificationsTabType,
  prepareHeaders,
  StoryCardArticleContent,
  StoryCardSection,
  StoryCardStatus,
  StoryStatus,
} from '../../../../services';
import {
  repostCard,
  storyCardNewsfeedPinnedStatusUpdate,
  storyCardStoryPinnedStatusUpdate,
  toggleCardSection,
} from '../../../../slices';
import { COPY_CUT_COOKIE_KEY, NOTIFICATIONS_ADMIN_COOKIE_KEY } from '../../../constants';
import { useAppDispatch, useAppTabsLabel, useRefreshFeeds } from '../../../hooks';
import { CopyCutAction } from '../../../models';
import {
  CookieActionType,
  CookieExpirationType,
  cookieOptions,
  downloadBlob,
  getOrganisationDomain,
} from '../../../utils';
import { Deeplink, DeeplinkType } from '../../Deeplink';
import { IconLabel } from '../../IconLabel';
import { CardActionsCommon, CardActionsProps } from '../CardActionsCommon';

import dropdownMenuClasses from '../../DropdownMenu/DropdownMenu.module.scss';

interface TogglePinHandlerArgs {
  action: Promise<boolean>;
  isPinned: boolean;
}

export const CardActionsAdmin: FunctionComponent<CardActionsProps> = memo(
  ({ card, isHorizontalFeedCard = false }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const { refreshFeeds } = useRefreshFeeds({ scrollTop: true });

    const { getAppTabsLabel } = useAppTabsLabel();

    const [, setCookie] = useCookies([COPY_CUT_COOKIE_KEY, NOTIFICATIONS_ADMIN_COOKIE_KEY]);

    const [isListOpen, setIsListOpen] = useState<boolean>(false);

    const {
      id: cardId,
      permissions: cardPermissions,
      isReadOnly: cardReadOnly,
      section: cardSection,
      status: cardStatus,
      isPinnedToTopOfNewsFeed,
      isPinnedToTopOfStory,
      story,
      content,
    } = card;

    const {
      allowToDelete: cardAllowToDelete,
      allowToPinToNewsfeed: cardAllowToPinToNewsfeed,
      allowToPinToStory: cardAllowToPinToStory,
    } = cardPermissions;

    const {
      id: storyId,
      isReadOnly: storyReadOnly,
      status: storyStatus,
      settings: storySettings,
    } = story;

    const { urlFormatted } = content as StoryCardArticleContent;

    const cardPublished = useMemo(() => cardStatus === StoryCardStatus.PUBLISHED, [cardStatus]);

    const storyPublished = useMemo(() => storyStatus === StoryStatus.PUBLISHED, [storyStatus]);

    const copyCutHandler = useCallback(
      (action: CopyCutAction) => {
        setCookie(
          COPY_CUT_COOKIE_KEY,
          {
            organisation: getOrganisationDomain(),
            storyId,
            cardId,
            cardReadOnly,
            action,
          },
          cookieOptions({ action: CookieActionType.SET, expiration: CookieExpirationType.DAY })
        );
        setIsListOpen(false);
      },
      [cardId, cardReadOnly, setCookie, storyId]
    );

    const copyHandler = useCallback(() => {
      copyCutHandler(CopyCutAction.COPY);
      toast(t('cardActions.copied'));
    }, [copyCutHandler, t]);

    const cutHandler = useCallback(() => {
      copyCutHandler(CopyCutAction.CUT);

      if (!isHorizontalFeedCard) {
        setTimeout(
          () =>
            document
              .querySelector('#cut-card')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          0
        );
      }
    }, [copyCutHandler, isHorizontalFeedCard]);

    const cardInWebFeed = useMemo(
      () => cardSection.includes(StoryCardSection.WEB_FEED),
      [cardSection]
    );

    const cardInAppFeed = useMemo(
      () => cardSection.includes(StoryCardSection.MOBILE_FEED),
      [cardSection]
    );

    const storyInWebFeed = useMemo(
      () => storySettings.includeInWebappNewsFeed,
      [storySettings.includeInWebappNewsFeed]
    );

    const storyInAppFeed = useMemo(
      () => storySettings.includeInNewsFeed,
      [storySettings.includeInNewsFeed]
    );

    const toggleInWebFeedLabel = `${cardInWebFeed ? 'X' : '✓'} ${getAppTabsLabel(
      ChannelAppTabAction.NEWS_FEED
    )}`;

    const toggleInAppFeedLabel = `${cardInAppFeed ? 'X' : '✓'} ${getAppTabsLabel(
      ChannelAppTabAction.NEWS_FEED
    )}`;

    const toggleInWebFeed = useCallback(async () => {
      setIsListOpen(false);

      try {
        const payload = await toast.promise(
          dispatch(
            toggleCardSection({
              storyId,
              storyCardId: cardId,
              fields: { includeInWebappNewsFeed: !cardInWebFeed },
            })
          ).unwrap(),
          {
            pending: t(`cardActions.${cardInWebFeed ? 'hiding' : 'showing'}-in-web-feed`),
            success: t(`cardActions.${cardInWebFeed ? 'hided' : 'showed'}-in-web-feed`),
            error: t(`cardActions.${cardInWebFeed ? 'hide' : 'show'}-in-web-feed-error`),
          }
        );

        if (!payload) {
          return;
        }

        refreshFeeds();
      } catch (_) {}
    }, [cardId, cardInWebFeed, dispatch, refreshFeeds, storyId, t]);

    const toggleInAppFeed = useCallback(async () => {
      setIsListOpen(false);

      try {
        const payload = await toast.promise(
          dispatch(
            toggleCardSection({
              storyId,
              storyCardId: cardId,
              fields: { includeInNewsFeed: !cardInAppFeed },
            })
          ).unwrap(),
          {
            pending: t(`cardActions.${cardInAppFeed ? 'hiding' : 'showing'}-in-app-feed`),
            success: t(`cardActions.${cardInAppFeed ? 'hided' : 'showed'}-in-app-feed`),
            error: t(`cardActions.${cardInAppFeed ? 'hide' : 'show'}-in-app-feed-error`),
          }
        );

        if (!payload) {
          return;
        }

        refreshFeeds();
      } catch (_) {}
    }, [cardId, cardInAppFeed, dispatch, refreshFeeds, storyId, t]);

    const togglePinHandler = useCallback(
      async ({ action, isPinned }: TogglePinHandlerArgs) => {
        setIsListOpen(false);

        try {
          const payload = await toast.promise(action, {
            pending: t(`cardActions.${isPinned ? 'unpinning' : 'pinning'}`),
            success: t(`cardActions.${isPinned ? 'unpinned' : 'pinned'}`),
            error: t(`cardActions.${isPinned ? 'unpin' : 'pin'}-error`),
          });

          if (!payload) {
            return;
          }

          refreshFeeds();
        } catch (_) {}
      },
      [refreshFeeds, t]
    );

    const newsFeedTogglePinHandler = useCallback(() => {
      togglePinHandler({
        action: dispatch(
          storyCardNewsfeedPinnedStatusUpdate({ storyId, storyCardId: cardId })
        ).unwrap(),
        isPinned: isPinnedToTopOfNewsFeed,
      });
    }, [cardId, dispatch, isPinnedToTopOfNewsFeed, storyId, togglePinHandler]);

    const storyFeedTogglePinHandler = useCallback(() => {
      togglePinHandler({
        action: dispatch(
          storyCardStoryPinnedStatusUpdate({ storyId, storyCardId: cardId })
        ).unwrap(),
        isPinned: isPinnedToTopOfStory,
      });
    }, [cardId, dispatch, isPinnedToTopOfStory, storyId, togglePinHandler]);

    const repostHandler = useCallback(async () => {
      setIsListOpen(false);

      try {
        const storyCard = await toast.promise(
          dispatch(repostCard({ storyId, storyCardId: cardId })).unwrap(),
          {
            pending: t('cardActions.reposting'),
            success: t('cardActions.reposted'),
            error: t('cardActions.repost-error'),
          }
        );

        if (!storyCard) {
          return;
        }

        refreshFeeds();
      } catch (_) {}
    }, [cardId, dispatch, refreshFeeds, storyId, t]);

    const pushHandler = useCallback(() => {
      setCookie(
        NOTIFICATIONS_ADMIN_COOKIE_KEY,
        { selectedTabIndex: NotificationsTabType.PUSH },
        cookieOptions()
      );

      navigate(`/admin/notifications?storyId=${storyId}&cardId=${cardId}`);
    }, [cardId, navigate, setCookie, storyId]);

    const downloadUserList = useCallback(async () => {
      try {
        const response = await fetch(
          `${getChannelUrl()}stories/${storyId}/items/${cardId}/riddle-state.csv`,
          { headers: prepareHeaders(new Headers()) }
        );
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        downloadBlob(await response.blob(), 'riddle-state.csv');
      } catch (error) {
        toast.error(t('cardActions.download-user-list-error'));
      }
    }, [cardId, storyId, t]);

    return (
      <CardActionsCommon
        card={card}
        isHorizontalFeedCard={isHorizontalFeedCard}
        isListOpen={isListOpen}
        setIsListOpen={setIsListOpen}
        adminActions={
          <>
            <IconLabel
              iconId={'copy'}
              iconSize={18}
              label={t('cardActions.copy')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={copyHandler}
              singleColor
            />

            {!storyReadOnly && cardAllowToDelete && (
              <IconLabel
                iconId={'cut'}
                iconSize={18}
                label={t('cardActions.cut')}
                className={dropdownMenuClasses['dropdown-menu__item--small']}
                onClick={cutHandler}
                singleColor
              />
            )}

            {!cardReadOnly && (
              <IconLabel
                iconId={'repost'}
                iconSize={18}
                label={t('cardActions.repost')}
                className={dropdownMenuClasses['dropdown-menu__item--small']}
                onClick={repostHandler}
                singleColor
              />
            )}

            <IconLabel
              iconId={'bell'}
              iconSize={18}
              label={t('cardActions.push')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={pushHandler}
              singleColor
            />

            <Deeplink
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              type={DeeplinkType.DRAFT}
              storyId={storyId}
              cardId={cardId}
              label={t('cardActions.preview-link')}
              onClick={() => setIsListOpen(false)}
            />

            <Deeplink
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              type={DeeplinkType.CARD}
              storyId={storyId}
              cardId={cardId}
              onClick={() => setIsListOpen(false)}
            />

            {urlFormatted?.includes('www.riddle.com') && (
              <IconLabel
                iconId={'users'}
                iconSize={18}
                label={t('cardActions.download-user-list')}
                className={dropdownMenuClasses['dropdown-menu__item--small']}
                onClick={downloadUserList}
                singleColor
              />
            )}

            {cardAllowToPinToNewsfeed && (
              <IconLabel
                iconId={'pin-n'}
                iconSize={18}
                label={t(`cardActions.${isPinnedToTopOfNewsFeed ? 'unpin' : 'pin'}`)}
                className={dropdownMenuClasses['dropdown-menu__item--small']}
                onClick={newsFeedTogglePinHandler}
                singleColor
              />
            )}

            {cardAllowToPinToStory && (
              <IconLabel
                iconId={'pin-m'}
                iconSize={18}
                label={t(`cardActions.story-${isPinnedToTopOfStory ? 'unpin' : 'pin'}`)}
                className={dropdownMenuClasses['dropdown-menu__item--small']}
                onClick={storyFeedTogglePinHandler}
                singleColor
              />
            )}

            {storyPublished && !storyReadOnly && cardPublished && !cardReadOnly && (
              <>
                {!storyInWebFeed && (
                  <IconLabel
                    iconId={'web'}
                    iconSize={18}
                    label={toggleInWebFeedLabel}
                    className={dropdownMenuClasses['dropdown-menu__item--small']}
                    onClick={toggleInWebFeed}
                    singleColor
                  />
                )}
                {!storyInAppFeed && (
                  <IconLabel
                    iconId={'mobile'}
                    iconSize={18}
                    label={toggleInAppFeedLabel}
                    className={dropdownMenuClasses['dropdown-menu__item--small']}
                    onClick={toggleInAppFeed}
                    singleColor
                  />
                )}
              </>
            )}
          </>
        }
      />
    );
  }
);
